import { type BookChapter } from './book';

// Web Speech inserts a gap at every utterance boundary, so chunks must end at sentence boundaries
// (where a small pause sounds natural) and never mid-sentence. We re-pack a chapter's paragraphs
// into larger, sentence-aligned chunks: source fragments that split mid-sentence (common in PDFs)
// get rejoined, and a chunk only ends on a sentence terminator. A single sentence longer than the
// target is kept whole rather than split — but a hard cap still applies so one runaway "sentence"
// (e.g. CJK text with no recognized terminator) can't become an utterance the synth truncates.

// Target chunk size. Larger = fewer (natural, sentence-end) pauses but coarser skip/resume; smaller
// = finer control. ~400 chars is a few sentences. One knob to tune the listening feel.
const TARGET_CHARS = 400;
// Absolute ceiling per chunk. Long utterances are unreliable in some engines (and CJK without
// terminators would otherwise be unbounded), so cap and hard-split beyond this.
const MAX_CHARS = 1000;

// Sentence terminators across scripts: Latin . ! ? plus CJK 。！？, ellipsis …, Arabic ؟, Devanagari ।
const TERMINATORS = '.!?。！？…؟।';
const SENTENCE_RE = new RegExp(
  `[^${TERMINATORS}]*[${TERMINATORS}]+["'’”)\\]]*\\s*|[^${TERMINATORS}]+$`,
  'gu',
);

/** Split text into sentence units, keeping terminal punctuation. A trailing run with no terminator
    (e.g. a PDF line break mid-sentence) comes back as its own unit so it can be rejoined. */
export function splitSentences(text: string): string[] {
  const trimmed = text.trim();
  if (trimmed === '') return [];
  const matches = trimmed.match(SENTENCE_RE);
  return matches ? matches.map((s) => s.trim()).filter(Boolean) : [trimmed];
}

/** Break an over-long string into pieces no longer than `max`, preferring to cut at a space (for
    space-less scripts like CJK there is no space, so it cuts at the character boundary). */
function hardSplit(text: string, max: number): string[] {
  if (text.length <= max) return [text];
  const parts: string[] = [];
  let rest = text;
  while (rest.length > max) {
    const space = rest.lastIndexOf(' ', max);
    const cut = space > max / 2 ? space : max; // only honor a space if it isn't absurdly early
    parts.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts;
}

/** Re-pack paragraphs into sentence-aligned chunks of roughly `target` characters (hard-capped). */
export function rechunkParagraphs(
  paragraphs: readonly string[],
  target: number = TARGET_CHARS,
  max: number = MAX_CHARS,
): string[] {
  const sentences: string[] = [];
  for (const p of paragraphs) sentences.push(...splitSentences(p));

  const chunks: string[] = [];
  let current = '';
  const flush = (): void => {
    if (current !== '') chunks.push(...hardSplit(current, max));
    current = '';
  };

  for (const sentence of sentences) {
    if (current === '') {
      current = sentence;
    } else if (current.length + 1 + sentence.length > target) {
      flush();
      current = sentence;
    } else {
      current = `${current} ${sentence}`;
    }
  }
  flush();
  return chunks;
}

/** Apply sentence-aligned re-chunking to every chapter, dropping any that end up empty. */
export function rechunkChapters(chapters: readonly BookChapter[]): BookChapter[] {
  return chapters
    .map((c) => ({ ...c, paragraphs: rechunkParagraphs(c.paragraphs) }))
    .filter((c) => c.paragraphs.length > 0);
}
